import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { MessageSquare, Users, GraduationCap, Calendar, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

/**
 * Lista de foros disponibles para el usuario actual.
 * Estudiante: foros de las clases en las que está inscrito (con foro habilitado).
 * Profesor:   foros de las clases que dicta + las en las que está inscrito.
 * Admin:      todos los foros activos.
 *
 * Click en un foro → /app/foros-clase/:claseId
 */
const MisForosClases = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [foros, setForos] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    []
  );

  // Banner estilo Pruebas/Videos: imagen de la clase con overlay oscuro o gradiente fallback
  const getBannerStyle = (bannerUrl) => {
    if (bannerUrl) {
      const imageUrl = bannerUrl.startsWith('http') ? bannerUrl : `${API}${bannerUrl}`;
      return {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.55)), url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    return { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/foros-clase/mis-foros`, { headers });
        if (!cancelled) setForos(res.data?.foros || []);
      } catch (e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, API, headers]);

  const handleAbrir = (foro) => {
    navigate(`/app/foros-clase/${foro.clase_id}`);
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="mis-foros-clases">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <MessageSquare className="h-6 w-6 text-indigo-600" />
          Foros de Clase
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Espacios privados de discusión para cada clase. Compartí ideas, hacé preguntas y dejá tu reflexión.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : foros.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center">
            <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">Sin foros disponibles</h3>
            <p className="text-sm text-gray-500">
              {user?.rol === 'estudiante' || user?.rol === 'pastor' || user?.rol === 'graduado'
                ? 'Aún no estás inscripto en clases con foro activo. El admin debe habilitar el foro de tu clase.'
                : 'No hay foros activos en este momento.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {foros.map(foro => (
            <Card
              key={foro.clase_id}
              onClick={() => handleAbrir(foro)}
              className="overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-indigo-300 group"
              data-testid={`foro-card-${foro.clase_id}`}
            >
              {/* Banner h-32 con imagen de la clase y overlay */}
              <div
                className="h-32 w-full relative"
                style={getBannerStyle(foro.clase_imagen)}
              >
                <div className="absolute inset-0 flex flex-col justify-between p-4">
                  <div className="flex items-start justify-between">
                    <div className="bg-white/20 backdrop-blur-sm p-1.5 rounded-lg">
                      <MessageSquare className="h-4 w-4 text-white" />
                    </div>
                    {foro.puede_moderar && (
                      <Badge className="bg-emerald-500/90 text-white border-0 text-[10px] backdrop-blur-sm">
                        Moderador
                      </Badge>
                    )}
                  </div>
                  <div>
                    <h3 className="text-white text-lg font-bold drop-shadow-lg leading-tight line-clamp-2">
                      {foro.clase_titulo}
                    </h3>
                    {foro.profesor_nombre && (
                      <p className="text-white/90 text-xs drop-shadow flex items-center gap-1 mt-1">
                        <GraduationCap className="h-3 w-3" />
                        {foro.profesor_nombre}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer con stats + CTA */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {foro.estudiantes_count} miembros
                    </span>
                    {foro.semestre && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Sem. {foro.semestre}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center text-indigo-600 text-xs font-semibold group-hover:translate-x-1 transition-transform">
                    Entrar
                    <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MisForosClases;
